import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

interface InlineEditProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export const InlineEdit: React.FC<InlineEditProps> = ({
  value,
  onChange,
  placeholder = "Enter text...",
  className,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  // Escape sets this before setIsEditing(false) triggers blur, so commit() is a no-op.
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  const commit = () => {
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    onChange(draft);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      inputRef.current?.blur();
    }
    if (e.key === "Escape") {
      cancelledRef.current = true;
      setDraft(value);
      setIsEditing(false);
    }
  };

  return (
    <div
      className={twMerge(
        "flex-1 rounded-lg border py-0.5 transition-all duration-150 cursor-pointer",
        isEditing
          ? "border-border-medium px-3 cursor-text"
          : "border-transparent hover:border-border-subtle hover:px-3",
        className,
      )}
      onClick={() => {
        if (!isEditing) {
          setDraft(value);
          setIsEditing(true);
        }
      }}
    >
      {isEditing ? (
        <input
          ref={inputRef}
          className="w-full bg-transparent text-sm font-medium text-text-primary outline-none border-none ring-0 focus:ring-0 p-0 placeholder:text-text-tertiary"
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={handleKeyDown}
        />
      ) : (
        <span className="text-sm font-medium text-text-primary">
          {value || <span className="text-text-tertiary">{placeholder}</span>}
        </span>
      )}
    </div>
  );
};
